import type { EventItem, EventProgram } from '@tc8l/nuxt-overview'
import type {
  GA4ViewItemListPayload,
  GA4SelectItemPayload,
  GA4ViewItemPayload,
  GA4BeginCheckoutPayload,
  GA4Item,
  ConvertItemsParams,
} from '~/types/tracking'

export const useTracking = () => {
  const { proxy } = useScriptGoogleTagManager()

  /**
   * Always clears the previous ecommerce object before pushing.
   * Prevents stale items[] bleeding into subsequent events.
   */
  function pushEcommerce(eventName: string, ecommerce: object) {
    proxy.dataLayer.push({ ecommerce: null })
    proxy.dataLayer.push({
      event: eventName,
      ecommerce: {
        currency: 'EUR',
        ...ecommerce,
      },
    })
  }

  function convertItems({ type, payload }: ConvertItemsParams, indexCalculation: number) {
    if (type === 'events') {
      trackViewItemList({
        item_list_id: 'events_overview',
        item_list_name: 'Events Overview',
        items: payload.map((event, index) => eventToGA4Item(event, index + indexCalculation)),
      })
    } else if (type === 'courses') {
      trackViewItemList({
        item_list_id: 'courses_overview',
        item_list_name: 'Courses Overview',
        items: payload.map((course, itemIndex) => courseToGA4Item(course, itemIndex)),
      })
    }
  }

  function convertBeginCheckout(event: EventItem, program?: EventProgram | null) {
    const item: GA4Item = {
      ...eventToGA4Item(event),
      item_variant: program?.startAt ?? 'program not selected yet',
      price: program?.price ?? undefined,
    }

    trackBeginCheckout({
      value: program?.price ?? undefined,
      items: [item],
    })
  }

  // ── Ecommerce ──────────────────────────────────────────────────────────────

  function trackViewItemList(payload: GA4ViewItemListPayload) {
    pushEcommerce('view_item_list', payload)
  }

  function trackSelectItem(payload: GA4SelectItemPayload) {
    pushEcommerce('select_item', payload)
  }

  function trackViewItem(payload: GA4ViewItemPayload) {
    pushEcommerce('view_item', payload)
  }

  function trackBeginCheckout(payload: GA4BeginCheckoutPayload) {
    pushEcommerce('begin_checkout', payload)
  }

  // ── Non-ecommerce ──────────────────────────────────────────────────────────

  function trackSearch(searchTerm: string) {
    proxy.dataLayer.push({
      event: 'search',
      search_term: searchTerm,
    })
  }
  function trackGenerateLead(formId?: number) {
    proxy.dataLayer.push({
      event: 'generate_lead',
      lead_source: 'Form ID:',
      formId,
    })
  }

  return {
    trackViewItemList,
    trackSelectItem,
    trackViewItem,
    trackBeginCheckout,
    trackSearch,
    trackGenerateLead,
    convertItems,
    convertBeginCheckout,
  }
}
